
import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CollabProps } from '../stores/collab_store';
import { RootStoreContext } from '../stores/root_store';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import { TimeUI } from './Date';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';

// import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import { CollabInviteProps } from '../stores/collab_invite_store';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { ColorPaletteProp, IconButton } from '@mui/joy';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';

import CollabInviteFeature from 'components/CollabInviteFeature';

import { Error, Success } from './FancyToast';

import useCollabInvite from 'hooks/useCollabInvite';
import useUserIdentity from 'hooks/useUserIdentity';
import useIdentity from 'hooks/useIdentity';
import useRoles from 'hooks/useRoles';

import { pluralize } from 'utils/misc';



const InviteItem = observer(({ invite }: { invite: CollabInviteProps }) => {
    const inviteStore = useContext(RootStoreContext).collabInviteStore;
    const { getIdentity, fetchIdentity } = useIdentity();
    const { currentIdentity } = useUserIdentity();
    const invitedIdentity = getIdentity(invite.identity);
    function onCancelClick() {
        inviteStore.doWithdrawCollabInvite(invite.key, invite.collab, currentIdentity.key)
            .then(() => {
                Success("Invite withdrawn");
            })
            .catch(() => {
                Error("Could not withdraw invite");
            });
    }

    useEffect(() => {
        if(!invitedIdentity && invite.identity) {
            fetchIdentity(currentIdentity.uuid, invite.identity);
        }
    }, [invite.identity, invitedIdentity, fetchIdentity]);

    const cancelButton = (['invited', 'available'].includes(invite.status) ?
        <Tooltip title={ invite.status == 'available' ?
            "Cancel and stop looking for an assistant" :
            "Cancel this invite. We won't let this person join." } arrow>
            <IconButton onClick={onCancelClick}><CancelOutlinedIcon /></IconButton>
        </Tooltip>
        : '')
    let color = "primary" as ColorPaletteProp;
    let style = {
        textDecoration: ''
    }
    switch (invite.status) {
        case 'withdrawn':
            style.textDecoration = 'line-through';
            color = "neutral"
            break;
        case 'available':
            color = "warning"
            break;
        case 'accepted':
            color = "success"
            break;
    }
    // if(invite.status === 'withdrawn') {
    //     style.textDecoration = 'line-through';
    //     color = "disabled"
    // }

    //  DefaultColorPalette = 'primary' | 'neutral' | 'danger' | 'success' | 'warning';

    return (
        <ListItem
            sx={style}
            endAction={cancelButton} >
            <Stack>
                {invite.status == "available" &&
                    <Tooltip arrow title="We're looking for someone to work on this. You can cancel this.">
                        <Typography color={color} level="body-sm">
                            Searching for assistant
                        </Typography>
                    </Tooltip>
                }
                {invite.status != "available" &&
                    <Typography color={color} level="body-sm">
                        {invitedIdentity?.key && invitedIdentity.name + ' ' } {invite.status}
                    </Typography>
                }

                <Typography color={color} level="body-xs">
                    <TimeUI ts={invite.date_created} />
                </Typography>

            </Stack>
        </ListItem>
    )
});

const _CollabInviteList = observer(({ collab }: { collab: CollabProps }) => {
    const {
        getInvitesForCollab,
        fetchInvitesForCollab,
    } = useCollabInvite();
    const { hasRole } = useRoles();

    const [showingInvites, setShowingInvites] = useState<CollabInviteProps[]>([]);
    const [showingAllInvites, setShowingAllInvites] = useState(false);

    if (!collab || !collab.key) {
        return null;
    }

    // by default: we only to show them when they're active in some way.
    // const showingInvites = showingAllInvites ? invites : activeInvites;
    const invites = getInvitesForCollab(collab.key);
    const activeInvites = getInvitesForCollab(collab.key).filter((invite) =>
        ['available', 'invited'].includes(invite.status)
    );
    const availableInvites = invites.filter((invite) => invite.status === 'available');



    useEffect(() => {
        setShowingInvites(showingAllInvites ? invites : activeInvites);
    }, [getInvitesForCollab, collab.uuid, invites.length]);

    useEffect(() => {
        if (collab.key) {
            fetchInvitesForCollab(collab.key);
        }
    }, [collab.key, fetchInvitesForCollab]);

    if(collab.variant == 'support' && !hasRole('support'))
        return null;

    return (
        <>

            <Stack
                sx={{ p: 1, s: 1 }}>
                <Stack

                    divider={<Divider orientation="vertical" />}>
                    {/* { invites.length === 0 && <Typography level="body-sm">No invites</Typography>} */}
                    {availableInvites.length > 0 &&
                        <Typography level="body-sm">
                            We're looking for <strong>{availableInvites.length}</strong> collaborator{availableInvites.length != 1 ? 's' : ''}.
                        </Typography>}

                </Stack>

                {showingInvites.length > 0 &&
                    <List>
                        {showingInvites.map((invite) =>
                            <InviteItem key={invite.key} invite={invite} />)}
                    </List>
                }

                {/* collab.variant == 'support' && hasRole('support') &&
                    <Stack pt={1} direction="row" justifyContent="center">
                        <Tooltip arrow title="Join this collab">
                            <Chip
                                size="sm"
                                color="primary"
                                variant="outlined"
                                onClick={() => {
                                    workKey && supportJoinCollab(workKey, collab.key);
                                }}>
                                Join Support Collab
                            </Chip>
                        </Tooltip>
                    </Stack>
                  */}

                {/* end of add collaborator variant check */}

            </Stack>


            <Stack>
                <CollabInviteFeature collab={collab}>

                {((invites.length != showingInvites.length) || showingAllInvites) &&
                    <Stack pt={1} direction="row" justifyContent="center">
                        <Tooltip arrow
                            placement="left"
                            title={
                                showingAllInvites ? "Hide the extra info" :
                                    "Show " + pluralize(invites.length - showingInvites.length, " more invite")
                            }>
                            <Chip
                                size="sm"
                                color="neutral"
                                variant="plain"
                                onClick={() => setShowingAllInvites(!showingAllInvites)}>
                                {showingAllInvites ? 'Hide history' : 'Show invite history'}
                            </Chip>
                        </Tooltip>
                    </Stack>

                }
                </CollabInviteFeature>
            </Stack>
        </>

    );
});

export default _CollabInviteList;
